import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PAStatus, PAUnitType, PALinkedVisit } from '../../../../dtos/patientmr/authorizations.dto';
import { AuthorizationsService, type PAEvvDriftResponse } from '../../../../services/patientmr/authorizations.service';
import { AlertPill } from '../../../common/Pills';
import { ProgressBar } from '../components/ProgressBar';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { LinkVisitsDialog } from './LinkVisitsDialog';
import { describeApiError } from '../../../../utils/apiErrors';

interface UsageTrackerSectionProps {
  /** When patientId + authId are provided, usage data is fetched from the API */
  patientId?: string;
  authId?: string;
  status?: PAStatus | string;
  /** Fallback props used when patientId/authId are not available */
  usedQuantity?: number;
  approvedQuantity?: number | null;
  approvedPerWeek?: number | null;
  daysLeft?: number | null;
  unitType?: PAUnitType | string;
  minutesPerUnit?: number | null;
  linkedVisits?: PALinkedVisit[];
  defaultOpen?: boolean;
  isLive?: boolean;
  /** PA-08: show Link visits / Unlink (needs PA update permission) */
  canManageLinks?: boolean;
  approvedStartDate?: string | null;
  approvedEndDate?: string | null;
  /** Called after a link/unlink so the parent can refresh the PA (used quantity, status) */
  onLinksChanged?: () => void;
}

export const UsageTrackerSection: React.FC<UsageTrackerSectionProps> = ({
  patientId,
  authId,
  status,
  usedQuantity: propUsed = 0,
  approvedQuantity: propApproved = null,
  approvedPerWeek: propApprovedPerWeek = null,
  daysLeft: propDaysLeft = null,
  unitType: propUnitType = PAUnitType.UNITS,
  minutesPerUnit: propMpu = null,
  linkedVisits: propLinkedVisits = [],
  defaultOpen = true,
  isLive,
  canManageLinks = false,
  approvedStartDate = null,
  approvedEndDate = null,
  onLinksChanged
}) => {
  const [reloadKey, setReloadKey] = useState(0);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<PALinkedVisit | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [showAllVisits, setShowAllVisits] = useState(false);

  // API-fetched usage state
  const [usedQuantity, setUsedQuantity] = useState<number>(propUsed);
  const [approvedQuantity, setApprovedQuantity] = useState<number | null>(propApproved);
  const [unitType, setUnitType] = useState<PAUnitType | string>(propUnitType);
  const [minutesPerUnit, setMinutesPerUnit] = useState<number | null>(propMpu);
  const [linkedVisits, setLinkedVisits] = useState<PALinkedVisit[]>(propLinkedVisits);
  const [remaining, setRemaining] = useState<number>(0);
  const [daysLeft, setDaysLeft] = useState<number | null>(propDaysLeft);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [evvDrift, setEvvDrift] = useState<PAEvvDriftResponse['evvDrift'] | null>(null);

  const shouldFetch = !!(
    patientId &&
    authId &&
    (status === PAStatus.APPROVED || status === PAStatus.EXPIRED || status === PAStatus.EXHAUSTED)
  );

  const liveBadge = isLive ?? status === PAStatus.APPROVED;

  useEffect(() => {
    if (!shouldFetch) return;

    let cancelled = false;
    setLoadingUsage(true);

    AuthorizationsService.getUsage(patientId!, authId!)
      .then(data => {
        if (cancelled) return;
        setUsedQuantity(data.usedQuantity);
        setApprovedQuantity(data.approvedQuantity);
        setUnitType(data.unitType);
        setMinutesPerUnit(data.minutesPerUnit);
        setLinkedVisits(data.linkedVisits ?? []);
        setRemaining(data.remaining);
      })
      .catch(() => {
        // Silently fall back to prop values
      })
      .finally(() => {
        if (!cancelled) setLoadingUsage(false);
      });

    return () => { cancelled = true; };
  }, [patientId, authId, status, shouldFetch, reloadKey]);

  const linkedVisitIds = useMemo(() => linkedVisits.map(v => v.visitId).filter(Boolean), [linkedVisits]);
  const canLink = canManageLinks && shouldFetch;

  const handleLinksChanged = () => {
    setReloadKey(k => k + 1);
    onLinksChanged?.();
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget || !patientId || !authId) return;
    setUnlinking(true);
    setLinkError(null);
    try {
      await AuthorizationsService.unlinkVisit(patientId, authId, unlinkTarget.visitId);
      setUnlinkTarget(null);
      handleLinksChanged();
    } catch (err) {
      setUnlinkTarget(null);
      setLinkError(describeApiError(err, 'Failed to unlink the visit.'));
    } finally {
      setUnlinking(false);
    }
  };

  // REQ-PA-19 PA drift (MVP since Phase 10)
  useEffect(() => {
    if (!patientId || !authId || !shouldFetch) return;
    let cancelled = false;
    AuthorizationsService.getEvvDrift(patientId, authId)
      .then((data) => {
        if (!cancelled) setEvvDrift(data.evvDrift);
      })
      .catch(() => {
        if (!cancelled) setEvvDrift(null);
      });
    return () => { cancelled = true; };
  }, [patientId, authId, shouldFetch]);

  // When not fetching from API, derive remaining from props
  useEffect(() => {
    if (!shouldFetch) {
      setRemaining((propApproved ?? 0) - propUsed);
    }
  }, [propApproved, propUsed, shouldFetch]);

  // Sync daysLeft from prop
  useEffect(() => {
    setDaysLeft(propDaysLeft);
  }, [propDaysLeft]);

  const hasHours = unitType === PAUnitType.UNITS && minutesPerUnit;
  const uLabel = AuthorizationsService.getUnitLabel(unitType);
  const pwLabel = AuthorizationsService.getPerWeekLabel(unitType);
  const visitsToShow = showAllVisits ? linkedVisits : linkedVisits.slice(0, 5);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50"
        type="button"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-sm font-semibold text-slate-800">Usage Tracker</span>
          {liveBadge && (
            <AlertPill type="success" text="Live" />
          )}
          {loadingUsage && (
            <span className="text-xs text-slate-400 animate-pulse">Loading...</span>
          )}
        </div>
      </button>

      {open && (
        <>
          {/* Summary cards */}
          {approvedQuantity != null && (
            <div className="grid grid-cols-4 gap-3 p-4 bg-gray-50/50 border-b border-gray-100">
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{pwLabel}</div>
                <div className="text-xl font-bold mt-1 text-slate-900">
                  {propApprovedPerWeek ?? '\u2014'}
                </div>
                {hasHours && propApprovedPerWeek && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {AuthorizationsService.toHours(propApprovedPerWeek, unitType, minutesPerUnit)} hrs
                  </div>
                )}
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Approved</div>
                <div className="text-xl font-bold mt-1 text-slate-900">{approvedQuantity}</div>
                {hasHours && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {AuthorizationsService.toHours(approvedQuantity, unitType, minutesPerUnit)} hrs
                  </div>
                )}
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Remaining</div>
                <div className="text-xl font-bold mt-1 text-emerald-600">{remaining}</div>
                {hasHours && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {AuthorizationsService.toHours(remaining, unitType, minutesPerUnit)} hrs
                  </div>
                )}
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Days Left</div>
                <div className={`text-xl font-bold mt-1 ${daysLeft != null && daysLeft <= 14 ? 'text-amber-600' : 'text-slate-900'}`}>
                  {daysLeft ?? '\u2014'}
                </div>
              </div>
            </div>
          )}

          <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-4">
            {evvDrift && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="pa-evv-drift">
                <p className="font-semibold text-amber-900">EVV usage tracker</p>
                <p className="mt-1 text-amber-800">Pelora visits: {evvDrift.peloraConsumedUnits ?? evvDrift.blackbirdConsumedUnits} units</p>
                <p className="text-amber-800">EVV-verified: {evvDrift.evvVerifiedUnits > 0 ? `${evvDrift.evvVerifiedUnits} units` : '—'}</p>
                <p className="text-amber-800">Pending reconciliation: {evvDrift.pendingReconciliationUnits ?? 0} units</p>
                {evvDrift.evvVerifiedUnits > 0 && <p className="text-amber-800">Drift: {evvDrift.driftUnits} units</p>}
                <p className="mt-1 text-xs text-amber-700">Informational. Recomputed after each EVV reconciliation run.</p>
              </div>
            )}
            <ProgressBar
              used={usedQuantity}
              total={approvedQuantity}
              unitType={unitType}
              minutesPerUnit={minutesPerUnit}
            />

            {canLink && (
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => { setLinkError(null); setLinkDialogOpen(true); }}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  Link visits
                </button>
              </div>
            )}
            {linkError && <p className="text-sm text-red-600" role="alert">{linkError}</p>}

            {linkedVisits.length > 0 && (
              <div>
                <div className="flex justify-between mb-2">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Linked Visits</h4>
                  <span className="text-xs text-slate-400">{linkedVisits.length} visits</span>
                </div>
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Date</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Clinician</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">
                          {hasHours ? `${uLabel.charAt(0).toUpperCase() + uLabel.slice(1)} (Hrs)` : uLabel.charAt(0).toUpperCase() + uLabel.slice(1)}
                        </th>
                        {canLink && <th className="px-3 py-2"><span className="sr-only">Actions</span></th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {visitsToShow.map((v, i) => (
                        <tr key={v.visitId || i} className="hover:bg-slate-50/50">
                          <td className="px-3 py-2 text-sm text-slate-700">{v.date}</td>
                          <td className="px-3 py-2 text-sm text-slate-600">{v.clinician}</td>
                          <td className="px-3 py-2 text-sm text-slate-500">{v.type}</td>
                          <td className="px-3 py-2 text-sm font-medium text-slate-800">
                            {v.unitsConsumed}
                            {hasHours && (
                              <span className="text-slate-400 font-normal ml-1">
                                ({AuthorizationsService.toHours(v.unitsConsumed, unitType, minutesPerUnit)}h)
                              </span>
                            )}
                          </td>
                          {canLink && (
                            <td className="px-3 py-2 text-right">
                              <button
                                type="button"
                                onClick={() => setUnlinkTarget(v)}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                Unlink
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {linkedVisits.length > 5 && !showAllVisits && (
                  <button
                    onClick={() => setShowAllVisits(true)}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                    type="button"
                  >
                    Show all {linkedVisits.length} visits
                  </button>
                )}
              </div>
            )}

            {linkedVisits.length === 0 && !loadingUsage && (
              <p className="text-sm text-slate-400 text-center py-2">No linked visits yet</p>
            )}
          </div>
        </>
      )}
      {canLink && patientId && authId && (
        <LinkVisitsDialog
          open={linkDialogOpen}
          patientId={patientId}
          authId={authId}
          linkedVisitIds={linkedVisitIds}
          approvedStartDate={approvedStartDate}
          approvedEndDate={approvedEndDate}
          onClose={() => setLinkDialogOpen(false)}
          onLinked={handleLinksChanged}
        />
      )}
      <ConfirmDialog
        open={unlinkTarget !== null}
        title="Unlink visit"
        message={`Unlink the visit on ${unlinkTarget?.date || ''} from this PA? Its ${unlinkTarget?.unitsConsumed ?? 0} ${uLabel} are returned to the authorization.`}
        confirmLabel="Unlink"
        destructive
        busy={unlinking}
        onConfirm={confirmUnlink}
        onCancel={() => setUnlinkTarget(null)}
      />
    </div>
  );
};

export default UsageTrackerSection;
